import React, { useEffect, useState, useCallback } from "react";
import { Send, FileText, ScrollText, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { VERIF_LABEL, VERIF_COLOR } from "./common";
import IdReview from "./IdReview";

const FILTERS = [
  ["needs", "Needs request"],
  ["requested", "Verification Pending"],
  ["pending", "Verification Submitted"],
  ["approved", "Verified"],
  ["rejected", "Rejected"],
  ["overridden", "Overridden"],
  ["all", "All reservations"],
];

export default function IdentityVerification() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("requested");
  const [idBooking, setIdBooking] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const load = useCallback(() => api.get("/admin/bookings").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const loadLogs = useCallback(() => {
    setLoadingLogs(true);
    api.get("/admin/id-audit-logs").then((r) => setLogs(r.data)).catch(() => setLogs([])).finally(() => setLoadingLogs(false));
  }, []);
  useEffect(() => { if (showLogs) loadLogs(); }, [showLogs, loadLogs]);

  const vsOf = (b) => b.verification_status || "none";
  const counts = FILTERS.reduce((acc, [k]) => {
    acc[k] = k === "all" ? list.length
      : k === "needs" ? list.filter((b) => ["none", ""].includes(vsOf(b))).length
        : list.filter((b) => vsOf(b) === k).length;
    return acc;
  }, {});
  const filtered = list.filter((b) =>
    filter === "all" ? true
      : filter === "needs" ? ["none", ""].includes(vsOf(b))
        : vsOf(b) === filter);

  const sendRequest = async (b) => {
    setBusyId(b.id);
    try { await api.post(`/admin/bookings/${b.id}/request-id`); toast.success(`Upload request emailed to ${b.guest_name}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  return (
    <>
      {idBooking && <IdReview booking={idBooking} onClose={() => setIdBooking(null)} onChange={load} />}

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="identity-filters">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`idfilter-${k}`}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${filter === k ? "bg-rose text-white" : "bg-sand text-forest hover:bg-rose/10"}`}>
            {label} <span className="opacity-70">({counts[k]})</span>
          </button>
        ))}
        <button onClick={() => setShowLogs((s) => !s)} data-testid="identity-toggle-logs"
          className="ml-auto text-xs px-3 py-1.5 rounded-full font-medium bg-forest text-cream inline-flex items-center gap-1.5"><ScrollText size={13} /> {showLogs ? "Hide audit log" : "Audit log"}</button>
      </div>

      {showLogs && (
        <div className="card-lux p-4 mb-4" data-testid="identity-audit-log">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-serif text-lg text-forest">ID access & action audit trail</h3>
            <button onClick={loadLogs} className="text-xs text-stone-500 inline-flex items-center gap-1 hover:text-rose"><RefreshCw size={13} /> Refresh</button>
          </div>
          {loadingLogs ? <Loader2 className="animate-spin text-rose mx-auto my-4" /> : logs.length === 0 ? (
            <p className="text-sm text-stone-400 py-3">No audit entries yet.</p>
          ) : (
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-xs" data-testid="identity-audit-table">
                <thead className="text-stone-400 uppercase tracking-wide"><tr><th className="text-left p-2">When (UTC)</th><th className="text-left p-2">User</th><th className="text-left p-2">Action</th><th className="text-left p-2">Booking</th><th className="text-left p-2">Reason</th></tr></thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-t border-stone-100">
                      <td className="p-2 whitespace-nowrap">{(l.timestamp || "").replace("T", " ").slice(0, 19)}</td>
                      <td className="p-2">{l.user}</td>
                      <td className="p-2 font-medium text-forest">{l.action}</td>
                      <td className="p-2 font-mono">{l.booking_code || l.booking_id?.slice(0, 8)}</td>
                      <td className="p-2 text-stone-500">{l.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="identity-table">
            <thead className="bg-sand/60 text-stone-500 text-xs uppercase tracking-wide">
              <tr><th className="text-left p-4">Guest</th><th className="text-left p-4">Suite</th><th className="text-left p-4">Dates</th><th className="text-left p-4">Verification</th><th className="text-left p-4">Actions</th></tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const vs = vsOf(b);
                return (
                  <tr key={b.id} className="border-t border-stone-100" data-testid={`identity-row-${b.id}`}>
                    <td className="p-4"><div className="font-medium text-forest">{b.guest_name}</div><div className="text-xs text-stone-400">{b.guest_email}</div></td>
                    <td className="p-4">{b.room_name}</td>
                    <td className="p-4 text-xs">{b.check_in}<br />→ {b.check_out}</td>
                    <td className="p-4"><span className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${VERIF_COLOR[vs] || "bg-stone-100 text-stone-500"}`} data-testid={`identity-status-${b.id}`}>{VERIF_LABEL[vs] || vs}</span></td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button onClick={() => sendRequest(b)} disabled={busyId === b.id} data-testid={`identity-request-${b.id}`} title="Send ID upload request" className="text-xs py-1.5 px-3 rounded-md bg-rose/10 text-rose font-semibold inline-flex items-center gap-1 disabled:opacity-50">
                          {busyId === b.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Request
                        </button>
                        <button onClick={() => setIdBooking(b)} data-testid={`identity-review-${b.id}`} title="Review / manage" className="text-xs py-1.5 px-3 rounded-md bg-stone-100 text-forest font-semibold inline-flex items-center gap-1 hover:bg-stone-200"><FileText size={13} /> Manage</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-stone-400">No reservations in this category.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
